/**
 * Événements Google Analytics du parcours utilisateur du simulateur.
 * Chaque fonction enveloppe trackEvent pour garantir des noms/paramètres cohérents.
 */

import { trackEvent } from './googleAnalytics';
import { supabase } from '../lib/supabaseClient';

const SESSION_KEY = 'app_session_id';

function getSessionId(): string {
  if (typeof window === 'undefined') return '';
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
}

function persistToSupabase(
  eventName: string,
  category: string,
  label: string | undefined,
  params: Record<string, unknown> | undefined
) {
  try {
    void supabase.from('analytics_events').insert({
      session_id: getSessionId(),
      event_name: eventName,
      category,
      label: label ?? '',
      params: params ?? {},
    });
  } catch (err) {
    console.error('Failed to persist analytics event', err);
  }
}

const CATEGORY = {
  lifecycle: 'simulator_lifecycle',
  navigation: 'wizard_navigation',
  profile: 'profile_config',
  primes: 'primes_config',
  engagement: 'engagement',
  conversion: 'conversion',
} as const;

function send(action: string, category: string, label?: string, params?: Record<string, unknown>) {
  trackEvent(action, category, label);
  if (params && typeof window !== 'undefined' && window.gtag) {
    try {
      window.gtag('event', action, { event_category: category, ...params });
    } catch (err) {
      console.error('analyticsEvents send error', err);
    }
  }
  persistToSupabase(action, category, label, params);
}

// 1. Lifecycle & navigation
export const trackSimulatorLoaded = (params: {
  profession: string;
  territory?: string;
  grade?: number | null;
  echelon?: number | null;
  embedMode?: boolean;
}) => send('simulator_loaded', CATEGORY.lifecycle, params.profession, params);

export const trackCookieConsent = (decision: 'accepted' | 'declined') =>
  send(`cookie_consent_${decision}`, CATEGORY.lifecycle, decision);

export const trackWizardStepViewed = (stepIndex: number, stepName: string) =>
  send('wizard_step_viewed', CATEGORY.navigation, stepName, { step_index: stepIndex });

export const trackWizardStepCompleted = (stepIndex: number, stepName: string, durationSeconds: number) =>
  send('wizard_step_completed', CATEGORY.navigation, stepName, {
    step_index: stepIndex,
    duration_seconds: durationSeconds,
  });

export const trackWizardBackClicked = (stepIndex: number, stepName: string) =>
  send('wizard_back_clicked', CATEGORY.navigation, stepName, { step_index: stepIndex });

// 2. Profil
export const trackProfessionSelected = (professionId: string) =>
  send('profession_selected', CATEGORY.profile, professionId);

export const trackStatusSelected = (statusType: string) =>
  send('status_selected', CATEGORY.profile, statusType);

export const trackTerritorySelected = (territoryId: string) =>
  send('territory_selected', CATEGORY.profile, territoryId);

export const trackGradeSelected = (gradeId: number, gradeLabel?: string) =>
  send('grade_selected', CATEGORY.profile, gradeLabel ?? String(gradeId), { grade_id: gradeId });

export const trackEchelonSelected = (echelonNumber: number, gradeId: number | null) =>
  send('echelon_selected', CATEGORY.profile, String(echelonNumber), {
    echelon_number: echelonNumber,
    grade_id: gradeId,
  });

// 3. Primes
export const trackPrimeToggled = (primeId: string, enabled: boolean) =>
  send('prime_toggled', CATEGORY.primes, primeId, { enabled });

export const trackPrimeAmountModified = (primeId: string, value: number) =>
  send('prime_amount_modified', CATEGORY.primes, primeId, { value });

export const trackNbiEnabled = (points: number, amount: number) =>
  send('nbi_enabled', CATEGORY.primes, 'nbi', { points, amount });

export const trackServicePrimeEnabled = () =>
  send('service_prime_enabled', CATEGORY.primes, 'prime_service');

export const trackSftConfigured = (nbEnfants: number) =>
  send('sft_configured', CATEGORY.primes, 'sft', { nb_enfants: nbEnfants });

// 4. Engagement & conversion
export const trackEstimationViewed = (params: {
  profession: string;
  grade?: number | null;
  echelon?: number | null;
  netMensuel?: number;
  brutMensuel?: number;
}) =>
  send('estimation_viewed', CATEGORY.engagement, params.profession, {
    grade: params.grade,
    echelon: params.echelon,
    net_mensuel: params.netMensuel,
    brut_mensuel: params.brutMensuel,
  });

export const trackSalaryComparisonOpened = () =>
  send('salary_comparison_opened', CATEGORY.engagement, 'salary_comparison');

export const trackFaqQuestionOpened = (questionId: string) =>
  send('faq_question_opened', CATEGORY.engagement, questionId);

export const trackPayCalendarLinkClicked = (source: string) =>
  send('pay_calendar_link_clicked', CATEGORY.engagement, source);

export const trackAppinesCtaClicked = (ctaLocation: string, destinationUrl: string) =>
  send('appines_cta_clicked', CATEGORY.conversion, ctaLocation, {
    destination_url: destinationUrl,
  });
